import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ApiKeysCard } from "@/components/ApiKeysCard";

const inputCls =
  "w-full px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-600 focus:outline-none focus:border-cyan-600 focus:ring-2 focus:ring-cyan-500/20 text-xs transition-all";

const ROLE_BADGE = {
  admin: "bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300 border-amber-700/50",
  employee: "bg-cyan-50 dark:bg-cyan-500/10 text-cyan-800 dark:text-cyan-300 border-cyan-300 dark:border-cyan-700/50",
  client: "bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700/50",
};

const PERM_KEYS = [
  ["generate", "Generate"],
  ["publish", "Publish"],
  ["download", "Download"],
];

export default function TeamPanel() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "employee" });
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const toggleActive = async (u) => {
    const next = u.active === false;
    try {
      await api.put(`/users/${u.id}/status`, { active: next });
      toast.success(next ? `${u.email} reactivated` : `${u.email} deactivated — login blocked`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`${u.email} deleted`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const togglePerm = async (u, key) => {
    const current = { generate: true, publish: true, download: true, ...(u.permissions || {}) };
    const next = { ...current, [key]: !current[key] };
    try {
      await api.put(`/users/${u.id}/permissions`, { permissions: next });
      toast.success(`${u.email}: ${key} ${next[key] ? "enabled" : "disabled"}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/users", form);
      toast.success(`${form.role === "employee" ? "Employee" : "Client"} account created for ${form.email}`);
      setForm({ name: "", email: "", password: "", role: "employee" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="team-panel">
      <div className="lg:col-span-4 space-y-6 h-fit">
      <form onSubmit={createUser} className="accent-bar premium-card p-6 rounded-xl bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-white/[0.08] space-y-4" data-testid="create-user-form">
        <div>
          <div className="text-xs uppercase code-font neon-text font-bold tracking-wider">Issue Access</div>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Create a username and password for an employee or a client.</p>
        </div>
        <div>
          <label className="block text-xs uppercase font-semibold text-slate-500 dark:text-slate-400 code-font mb-2" htmlFor="team-name">Full Name</label>
          <input id="team-name" className={inputCls} required placeholder="Priya Sharma" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="team-name-input" />
        </div>
        <div>
          <label className="block text-xs uppercase font-semibold text-slate-500 dark:text-slate-400 code-font mb-2" htmlFor="team-email">Email (username)</label>
          <input id="team-email" className={inputCls} required type="email" placeholder="priya@dreamssoftsolutions.com" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="team-email-input" />
        </div>
        <div>
          <label className="block text-xs uppercase font-semibold text-slate-500 dark:text-slate-400 code-font mb-2" htmlFor="team-password">Password</label>
          <input id="team-password" className={inputCls} required type="text" minLength="6" placeholder="Min 6 characters" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="team-password-input" />
        </div>
        <div>
          <label className="block text-xs uppercase font-semibold text-slate-500 dark:text-slate-400 code-font mb-2" htmlFor="team-role">Access Level</label>
          <select id="team-role" className={inputCls} value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="team-role-select">
            <option value="employee">Employee — workbench &amp; project access</option>
            <option value="client">Client — own project preview only</option>
          </select>
        </div>
        <button type="submit" disabled={creating}
          className="w-full px-4 py-2.5 rounded-xl bg-gradient-to-r from-cyan-600 to-sky-600 dark:from-cyan-400 dark:to-sky-400 text-white dark:text-[#090D16] font-bold uppercase text-xs tracking-wider hover:from-cyan-700 hover:to-sky-700 dark:hover:from-cyan-300 dark:hover:to-sky-300 shadow-md shadow-cyan-600/20 dark:shadow-cyan-400/20 transition-all disabled:opacity-60"
          data-testid="create-user-button">
          {creating ? "Creating…" : "Create Account"}
        </button>
      </form>
      <ApiKeysCard />
      </div>

      <div className="lg:col-span-8 accent-bar premium-card rounded-xl bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-white/[0.08] overflow-hidden h-fit" data-testid="users-table-card">
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
          <h2 className="text-sm font-bold text-slate-900 dark:text-white code-font uppercase tracking-wider">Portal Accounts</h2>
          <span className="text-xs text-slate-500 dark:text-slate-400 code-font" data-testid="users-count">{users.length} total</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="text-left text-xs code-font uppercase text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
                <th className="px-6 py-3">Name</th>
                <th className="px-6 py-3">Email</th>
                <th className="px-6 py-3">Access</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr><td colSpan="5" className="px-6 py-6 text-slate-500 dark:text-slate-400 text-xs code-font" data-testid="users-empty">No accounts yet.</td></tr>
              )}
              {users.map((u) => {
                const isActive = u.active !== false;
                const isSelf = me && u.id === me._id;
                return (
                <tr key={u.id} className="border-b border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300" data-testid={`user-row-${u.id}`}>
                  <td className="px-6 py-3 text-slate-900 dark:text-white">{u.name}</td>
                  <td className="px-6 py-3 code-font text-xs">{u.email}</td>
                  <td className="px-6 py-3">
                    <span className={`px-2 py-0.5 rounded border text-xs code-font uppercase ${ROLE_BADGE[u.role] || ROLE_BADGE.client}`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="px-6 py-3">
                    <span className={`px-2 py-0.5 rounded border text-xs code-font uppercase ${
                      isActive ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700/40" : "bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-700/40"
                    }`} data-testid={`user-status-${u.id}`}>
                      {isActive ? "Active" : "Deactivated"}
                    </span>
                  </td>
                  <td className="px-6 py-3">
                    {!isSelf && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => toggleActive(u)}
                          className={`text-xs px-2.5 py-1 rounded border code-font transition-colors ${
                            isActive
                              ? "bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-700/40 hover:bg-amber-100 dark:hover:bg-amber-500/20"
                              : "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700/40 hover:bg-emerald-100 dark:hover:bg-emerald-500/20"
                          }`}
                          data-testid={`toggle-user-${u.id}`}
                        >
                          {isActive ? "Deactivate" : "Reactivate"}
                        </button>
                        <button
                          onClick={() => removeUser(u)}
                          className="text-xs px-2.5 py-1 rounded border bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-700/40 hover:bg-red-500/20 code-font transition-colors"
                          data-testid={`delete-user-${u.id}`}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                    {u.role === "employee" && !isSelf && (
                      <div className="flex gap-1 mt-2" data-testid={`perm-toggles-${u.id}`}>
                        {PERM_KEYS.map(([key, label]) => {
                          const perms = { generate: true, publish: true, download: true, ...(u.permissions || {}) };
                          const on = perms[key] !== false;
                          return (
                            <button
                              key={key}
                              onClick={() => togglePerm(u, key)}
                              className={`text-[10px] px-2 py-0.5 rounded border code-font transition-colors ${
                                on
                                  ? "bg-cyan-500/15 text-cyan-800 dark:text-cyan-300 border-cyan-200 dark:border-cyan-700/40 hover:bg-cyan-500/25"
                                  : "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-300 dark:border-slate-700 hover:bg-slate-200"
                              }`}
                              data-testid={`perm-${key}-${u.id}`}
                            >
                              {label} {on ? "✓" : "✗"}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
